export type Tab = 'home' | 'domains' | 'servers' | 'balance' | 'profile';

/** What a top-up for a missing amount was for: placed by itself once the money arrives. */
export type Purchase =
  | { kind: 'domainOrder'; domain: string; contactId?: string; label: string }
  | {
      kind: 'serverOrder';
      planId: string;
      imageId: string;
      name: string;
      sshPublicKey?: string;
      vpn?: 'vless' | 'amneziawg' | 'outline' | 'wireguard';
      label: string;
    }
  | { kind: 'domainRenew'; id: string; label: string }
  | { kind: 'serverRenew'; id: string; label: string };

/** Screens pushed over a tab (hide the tab bar, show the Back button). */
export type Pushed =
  | { name: 'topup'; amountCents?: number; after?: Purchase }
  | { name: 'domain'; id: string }
  | { name: 'serverCreate'; vpn?: boolean }
  | { name: 'server'; id: string }
  | { name: 'vpnTrial' }
  | { name: 'vpnSubscription' }
  | { name: 'admin' }
  | { name: 'adminUsers' }
  | { name: 'adminDomains' }
  | { name: 'adminServers' }
  | { name: 'adminUser'; id: string }
  | { name: 'adminOrders'; filter?: 'attention' }
  | { name: 'adminBroadcasts' }
  | { name: 'adminPromos' }
  | { name: 'adminProviders' }
  | { name: 'adminServerCatalog' }
  | { name: 'adminVpnSettings' }
  | { name: 'adminVpnStatus' }
  | { name: 'adminSystemErrors' }
  | { name: 'gift' }
  | {
      name: 'domainCheckout';
      domain: string;
      label: string;
      tld: string;
      price: number;
      renew: number | null;
    };

export interface Nav {
  tab: (t: Tab) => void;
  push: (s: Pushed) => void;
  back: () => void;
  /** Swap the top pushed screen (e.g. checkout → the thing just bought). */
  replace: (s: Pushed) => void;
  /** Close pushed screens and go to a tab (home by default). */
  reset: (t?: Tab) => void;
}

const TABS: Tab[] = ['home', 'domains', 'servers', 'balance', 'profile'];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Where to open the app: `?s=` from bot buttons. "servers" opens a tab; "server.<id>", "domain.<id>",
 * "topup.<cents>" and "serverCreate" open a screen over the matching tab; "admin" opens the admin panel
 * over the profile (the API decides who may see it). Anything else is home.
 */
export function initialRoute(search: string): { tab: Tab; stack: Pushed[]; query?: string } {
  const s = new URLSearchParams(search).get('s') ?? '';
  const [name, arg = ''] = s.split('.');
  if (TABS.includes(name as Tab)) return { tab: name as Tab, stack: [] };
  if (name === 'server' && UUID.test(arg)) return { tab: 'servers', stack: [{ name: 'server', id: arg }] };
  if (name === 'domain' && UUID.test(arg)) return { tab: 'domains', stack: [{ name: 'domain', id: arg }] };
  // search.mybrand.com: the domains tab with the name already searched
  if (name === 'search' && arg) return { tab: 'domains', stack: [], query: s.slice('search.'.length) };
  if (name === 'admin') return { tab: 'profile', stack: [{ name: 'admin' }] };
  if (name === 'serverCreate') return { tab: 'servers', stack: [{ name: 'serverCreate' }] };
  if (name === 'vpnTrial') return { tab: 'home', stack: [{ name: 'vpnTrial' }] };
  if (name === 'vpnSubscription') return { tab: 'home', stack: [{ name: 'vpnSubscription' }] };
  if (name === 'topup') {
    const cents = Number(arg);
    return {
      tab: 'balance',
      stack: [{ name: 'topup', amountCents: Number.isSafeInteger(cents) && cents > 0 ? cents : undefined }],
    };
  }
  return { tab: 'home', stack: [] };
}
